import { useCallback,useEffect,useMemo,useState } from "react";
import {
  Box,Typography,Button,CircularProgress,Table,TableBody,TableCell,TableContainer,
  TableHead,TableRow,Dialog,DialogTitle,DialogContent,DialogActions
} from "@mui/material";
import { useAppTheme } from "../../../../context/ThemeContext";
import { useAlert } from "../../../../context/AlertContext";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const formatDate=(value)=>{
  if(!value) return "—";
  const parts=String(value).split("-");
  if(parts.length!==3) return value;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatTime=(value)=>{
  if(!value) return "—";
  return String(value).slice(0,5);
};

const formatPhase=(value)=>{
  if(value==="LEAGUE") return "Todos contra todos";
  if(value==="GROUPS") return "Fase de grupos";
  if(value==="KNOCKOUT") return "Eliminación directa";
  return value||"—";
};

const getTeamName=(team)=>{
  return team?.name||team?.team_name||team?.team?.name||"—";
};

const getTeamLogo=(team)=>{
  return team?.logo||team?.team_logo||team?.team?.logo||null;
};

const getStatus=(match)=>{
  if(match.status_display) return match.status_display;
  if(match.status==="SCHEDULED") return "Programado";
  if(match.status==="LIVE") return "En vivo";
  if(match.status==="FINISHED") return "Finalizado";
  if(match.status==="CANCELLED") return "Cancelado";
  if(match.status==="SUSPENDED") return "Suspendido";
  return match.status||"—";
};

export default function MatchSchedule({
  tournamentSlug,
  categorySlug,
  phase,
  groups=[],
  teams=[]
}){
  const {colors,typography}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [matches,setMatches]=useState([]);
  const [loading,setLoading]=useState(true);
  const [generating,setGenerating]=useState(false);
  const [confirmGenerate,setConfirmGenerate]=useState(false);

  const loadMatches=useCallback(async()=>{
    if(!tournamentSlug||!categorySlug||!phase?.slug){
      setMatches([]);
      setLoading(false);
      return;
    }

    try{
      setLoading(true);

      const response=await fetch(
        `/matches/tournaments/${tournamentSlug}/categories/${categorySlug}/phases/${phase.slug}/fixture/`,
        {credentials:"include"}
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudo cargar el calendario de partidos."
        );
      }

      const rows=Array.isArray(data)
        ?data
        :Array.isArray(data.matches)
          ?data.matches
          :[];

      setMatches(rows);
    }catch(error){
      setMatches([]);
      showError(error.message||"No se pudo cargar el calendario de partidos.");
    }finally{
      setLoading(false);
    }
  },[
    tournamentSlug,
    categorySlug,
    phase?.slug,
    showError
  ]);

  useEffect(()=>{
    loadMatches();
  },[loadMatches]);

  const selectedTeams=useMemo(()=>{
    return teams.filter((item)=>item.selected);
  },[teams]);

  const activeGroups=useMemo(()=>{
    return groups.filter((group)=>group.active!==false);
  },[groups]);

  const validateGeneration=()=>{
    if(selectedTeams.length<2){
      showError("La fase necesita al menos dos equipos para generar el calendario.");
      return false;
    }

    if(phase.format==="GROUPS"){
      if(activeGroups.length===0){
        showError("Primero debes configurar al menos un grupo activo.");
        return false;
      }

      if(selectedTeams.some((item)=>!item.group)){
        showError("Todos los equipos seleccionados deben estar asignados a un grupo.");
        return false;
      }

      const invalidGroup=activeGroups.some((group)=>{
        const count=selectedTeams.filter((item)=>item.group===group.slug).length;
        return count<2;
      });

      if(invalidGroup){
        showError("Cada grupo activo debe tener al menos dos equipos.");
        return false;
      }
    }

    if(phase.format==="KNOCKOUT"&&selectedTeams.length%2!==0){
      showError("La eliminación directa necesita una cantidad par de equipos.");
      return false;
    }

    return true;
  };

  const openGenerateDialog=()=>{
    if(matches.length>0){
      showError("Esta fase ya tiene un calendario generado.");
      return;
    }

    if(!validateGeneration()) return;

    setConfirmGenerate(true);
  };

  const closeGenerateDialog=()=>{
    if(generating) return;
    setConfirmGenerate(false);
  };

  const generateSchedule=async()=>{
    try{
      setGenerating(true);

      const response=await fetch(
        `/matches/tournaments/${tournamentSlug}/categories/${categorySlug}/phases/${phase.slug}/fixture/generate/`,
        {
          method:"POST",
          credentials:"include",
          headers:{
            "X-CSRFToken":getCookie("csrftoken")
          }
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          data.detail||
          "No se pudo generar el calendario de partidos."
        );
      }

      setConfirmGenerate(false);
      await loadMatches();

      showSuccess(
        data.message||
        "Calendario de partidos generado correctamente."
      );
    }catch(error){
      showError(error.message||"No se pudo generar el calendario de partidos.");
    }finally{
      setGenerating(false);
    }
  };

  const groupedMatches=useMemo(()=>{
    const result=[];

    matches.forEach((match)=>{
      const groupSlug=
        match.group?.slug||
        match.group_slug||
        "";

      const groupName=
        match.group?.name||
        match.group_name||
        "";

      const roundOrder=
        match.round?.order??
        match.round_order??
        0;

      const roundName=
        match.round?.name||
        match.round_name||
        "Jornada";

      const leg=
        match.round?.leg??
        match.leg??
        1;

      let section=result.find((item)=>
        item.groupSlug===groupSlug&&
        item.roundOrder===roundOrder&&
        item.leg===leg
      );

      if(!section){
        section={
          key:`${groupSlug||"phase"}-${roundOrder}-${leg}`,
          groupSlug,
          groupName,
          roundOrder,
          roundName,
          leg,
          matches:[]
        };

        result.push(section);
      }

      section.matches.push(match);
    });

    return result.sort((a,b)=>{
      if(a.groupName!==b.groupName){
        return a.groupName.localeCompare(
          b.groupName,
          "es",
          {
            sensitivity:"base",
            numeric:true
          }
        );
      }

      if(a.roundOrder!==b.roundOrder){
        return a.roundOrder-b.roundOrder;
      }

      return a.leg-b.leg;
    });
  },[matches]);

  if(loading){
    return(
      <Box
        sx={{
          minHeight:120,
          display:"flex",
          alignItems:"center",
          justifyContent:"center"
        }}
      >
        <CircularProgress
          size={24}
          sx={{color:colors.secondary}}
        />
      </Box>
    );
  }

  return(
    <Box sx={{width:"100%"}}>
      <Box
        sx={{
          mb:0.8,
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:typography.sizes.md,
              fontWeight:700,
              color:colors.text
            }}
          >
            Calendario de partidos
          </Typography>

          <Typography
            sx={{
              mt:0.2,
              fontSize:typography.sizes.sm,
              color:colors.textSecondary
            }}
          >
            {matches.length>0
              ?`${matches.length} partido${matches.length===1?"":"s"} programado${matches.length===1?"":"s"}`
              :`${formatPhase(phase.format)} · Calendario aún no generado`
            }
          </Typography>
        </Box>

        {matches.length===0&&(
          <Button
            size="small"
            variant="contained"
            onClick={openGenerateDialog}
            disabled={generating}
            sx={{
              px:2,
              textTransform:"none",
              backgroundColor:colors.secondary,
              "&:hover":{
                backgroundColor:colors.secondaryHover
              }
            }}
          >
            Generar calendario
          </Button>
        )}
      </Box>

      {matches.length===0?(
        <TableContainer
          sx={{
            border:`1px solid ${colors.border}`,
            borderRadius:"10px",
            backgroundColor:colors.surface,
            boxShadow:"none",
            overflow:"hidden"
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{backgroundColor:colors.tableHeader}}>
                <TableCell
                  sx={{
                    color:colors.tableHeaderText,
                    fontWeight:700
                  }}
                >
                  Calendario
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    py:3,
                    color:colors.textSecondary
                  }}
                >
                  Todavía no se han generado los partidos de esta fase.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ):(
        <Box
          sx={{
            display:"flex",
            flexDirection:"column",
            gap:1.2
          }}
        >
          {groupedMatches.map((section)=>(
            <Box key={section.key}>
              <Box
                sx={{
                  mb:0.6,
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"space-between",
                  gap:1
                }}
              >
                <Typography
                  sx={{
                    fontSize:typography.sizes.sm,
                    fontWeight:700,
                    color:colors.text
                  }}
                >
                  {section.groupName
                    ?`${section.groupName} · ${section.roundName}`
                    :section.roundName
                  }
                </Typography>

                {phase.round_trip&&(
                  <Typography
                    sx={{
                      fontSize:typography.sizes.sm,
                      color:colors.textSecondary
                    }}
                  >
                    {section.leg===2?"Vuelta":"Ida"}
                  </Typography>
                )}
              </Box>

              <TableContainer
                sx={{
                  border:`1px solid ${colors.border}`,
                  borderRadius:"10px",
                  backgroundColor:colors.surface,
                  boxShadow:"none",
                  overflowX:"auto"
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth:760
                  }}
                >
                  <TableHead>
                    <TableRow sx={{backgroundColor:colors.tableHeader}}>
                      <TableCell
                        sx={{
                          color:colors.tableHeaderText,
                          fontWeight:700
                        }}
                      >
                        Local
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          color:colors.tableHeaderText,
                          fontWeight:700,
                          width:55
                        }}
                      >
                        VS
                      </TableCell>

                      <TableCell
                        sx={{
                          color:colors.tableHeaderText,
                          fontWeight:700
                        }}
                      >
                        Visitante
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          color:colors.tableHeaderText,
                          fontWeight:700,
                          whiteSpace:"nowrap"
                        }}
                      >
                        Fecha
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          color:colors.tableHeaderText,
                          fontWeight:700,
                          whiteSpace:"nowrap"
                        }}
                      >
                        Hora
                      </TableCell>

                      <TableCell
                        sx={{
                          color:colors.tableHeaderText,
                          fontWeight:700
                        }}
                      >
                        Cancha
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          color:colors.tableHeaderText,
                          fontWeight:700,
                          whiteSpace:"nowrap"
                        }}
                      >
                        Estado
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {section.matches.map((match)=>(
                      <TableRow
                        key={match.code}
                        hover
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display:"flex",
                              alignItems:"center",
                              gap:1
                            }}
                          >
                            {getTeamLogo(match.local)&&(
                              <Box
                                component="img"
                                src={getTeamLogo(match.local)}
                                alt={getTeamName(match.local)}
                                sx={{
                                  width:28,
                                  height:28,
                                  objectFit:"contain",
                                  flexShrink:0
                                }}
                              />
                            )}

                            <Typography
                              sx={{
                                fontSize:typography.sizes.md,
                                fontWeight:600,
                                color:colors.text
                              }}
                            >
                              {getTeamName(match.local)}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            fontSize:typography.sizes.sm,
                            color:colors.textSecondary
                          }}
                        >
                          VS
                        </TableCell>

                        <TableCell>
                          <Box
                            sx={{
                              display:"flex",
                              alignItems:"center",
                              gap:1
                            }}
                          >
                            {getTeamLogo(match.visitor)&&(
                              <Box
                                component="img"
                                src={getTeamLogo(match.visitor)}
                                alt={getTeamName(match.visitor)}
                                sx={{
                                  width:28,
                                  height:28,
                                  objectFit:"contain",
                                  flexShrink:0
                                }}
                              />
                            )}

                            <Typography
                              sx={{
                                fontSize:typography.sizes.md,
                                fontWeight:600,
                                color:colors.text
                              }}
                            >
                              {getTeamName(match.visitor)}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            whiteSpace:"nowrap",
                            color:colors.text
                          }}
                        >
                          {formatDate(match.date)}
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            whiteSpace:"nowrap",
                            color:colors.text
                          }}
                        >
                          {formatTime(match.start_time)}
                        </TableCell>

                        <TableCell
                          sx={{
                            color:colors.text,
                            whiteSpace:"nowrap"
                          }}
                        >
                          {match.venue?.name||match.venue_name||"—"}
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            whiteSpace:"nowrap",
                            color:colors.text
                          }}
                        >
                          {getStatus(match)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={confirmGenerate}
        onClose={closeGenerateDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:700,
              color:colors.text
            }}
          >
            Generar calendario
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography
            sx={{
              pt:0.5,
              fontSize:typography.sizes.md,
              color:colors.text
            }}
          >
            Se generarán automáticamente los partidos de {phase.name}.
            {phase.format==="GROUPS"&&
              " Se utilizarán los grupos y equipos actualmente configurados."
            }
          </Typography>

          <Typography
            sx={{
              mt:1,
              fontSize:typography.sizes.sm,
              color:colors.textSecondary
            }}
          >
            Una vez generado el calendario no podrá volver a sortearse mientras existan partidos activos en esta fase.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            size="small"
            disabled={generating}
            onClick={closeGenerateDialog}
            sx={{
              textTransform:"none",
              color:colors.textSecondary
            }}
          >
            Cancelar
          </Button>

          <Button
            size="small"
            variant="contained"
            disabled={generating}
            onClick={generateSchedule}
            sx={{
              textTransform:"none",
              backgroundColor:colors.secondary,
              "&:hover":{
                backgroundColor:colors.secondaryHover
              }
            }}
          >
            {generating?"Generando...":"Generar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}